/**
 * One of the optional extras above the payment methods — the company receipt
 * and the coupon.
 *
 * They were two full-width ghost pills, each a label and nothing else: a
 * customer could not tell what pressing one would ask of them, a disabled one
 * was a faded word with no reason, and at the width of the panel they read as
 * two more payment methods rather than as something you add to one. So they
 * are a pair of tiles now, side by side, deliberately quieter than the method
 * rows below: an icon, a name, one line on what it is for, and a `+` that says
 * "adds something" rather than "pays".
 *
 * Applied, the tile turns to the brand's soft fill with a tick and shows what
 * was applied — the company's name, the code — so the customer can check it
 * without reopening the sheet, and carries its own remove key.
 */
import type { ReactNode } from 'react'
import { Icon } from '@/components/icons'
import type { IconName } from '@/components/icons'
import { Plaque } from '@/components/ui'
import { useT } from '@/i18n'

export function PaymentExtra({
  icon,
  label,
  hint,
  unavailable,
  onOpen
}: {
  icon: IconName
  label: string
  hint: string
  /** Why it cannot be opened right now. Shown in place of the hint. */
  unavailable?: string | null
  onOpen: () => void
}) {
  const off = unavailable != null
  return (
    <button
      type="button"
      disabled={off}
      onClick={onOpen}
      className="tap flex min-w-0 items-center gap-5 rounded-[var(--radius-tile)] border-2 border-[var(--color-line)] bg-[var(--color-paper-raised)] p-5 pr-6 text-left disabled:cursor-not-allowed disabled:bg-[var(--color-paper-sunk)] active:border-[var(--color-brand)] active:bg-[var(--color-brand-soft)]"
    >
      <Plaque icon={icon} tone="paper" size="small" className={off ? 'opacity-50' : ''} />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={`display-upright text-[length:var(--text-kiosk)] leading-tight ${off ? 'text-[var(--color-ink-faint)]' : ''}`}
        >
          {label}
        </span>
        <span className="text-[length:var(--text-kiosk-sm)] leading-snug text-[var(--color-ink-soft)]">
          {off ? unavailable : hint}
        </span>
      </span>
      {!off && (
        <span className="grid size-14 shrink-0 place-items-center rounded-full border-2 border-[var(--color-ink)] text-[var(--color-ink)]">
          <Icon name="plus" className="size-7" />
        </span>
      )}
    </button>
  )
}

export function AppliedExtra({
  label,
  children,
  onRemove
}: {
  label: string
  /** What was applied: the company's name, the code. */
  children: ReactNode
  /** Absent when it can no longer be taken off. */
  onRemove?: () => void
}) {
  const t = useT()
  return (
    <div className="flex min-w-0 items-center gap-5 rounded-[var(--radius-tile)] border-2 border-[var(--color-brand)] bg-[var(--color-brand-soft)] p-5 pr-4">
      <Plaque icon="check" tone="dark" size="small" />
      <div className="min-w-0 flex-1">
        <p className="eyebrow truncate">{label}</p>
        <div className="mt-1 min-w-0">{children}</div>
      </div>
      {onRemove !== undefined && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={t('cart.remove')}
          className="tap grid size-18 shrink-0 place-items-center rounded-full text-[var(--color-ink-soft)] active:bg-[var(--color-brand)] active:text-[var(--color-brand-ink)]"
        >
          <Icon name="close" className="size-8" />
        </button>
      )}
    </div>
  )
}
